import { Button, Menu } from '@cma/common'
import { HelixIcon } from '@helix/helix-icon'
import {
  eye,
  folder,
  more_horizontal,
  outbox,
  pencil,
  share,
  trash_can
} from '@helix/helix-icon/outlined'
import { ReactNode, useState } from 'react'
import { useContent } from './ContentContext'
import { FolderSubMenu } from './FolderSubMenu'
import { ShareContentModal } from './share_modal/ShareContentModal'
import { useRemoveMenuOverflow } from './utils'

interface ContentFileMenuProps {
  id: string
  type: 'Folder' | 'PlatformClientContent'
  direction?: 'left' | 'center' | 'right'
  buttonClassName?: string
  buttonIcon?: ReactNode
  disabled?: boolean
  onRename?: () => void
  onDelete?: () => void
  onPreview?: () => void
}

export function ContentFileMenu({
  buttonIcon = <HelixIcon icon={more_horizontal} className="h-4 w-4" />,
  buttonClassName = '',
  direction = 'right',
  disabled = false,
  id,
  type,
  onDelete,
  onRename,
  onPreview
}: ContentFileMenuProps) {
  const {
    uploadRef,
    isRenaming,
    currentFolder,
    currentFolderId,
    viewMode,
    navigateToFolder
  } = useContent()
  const [showSharingModal, setShowSharingModal] = useState(false)
  const [managePermissions, setManagePermissions] = useState<string | null>(
    null
  )
  const [menuKey, setMenuKey] = useState(0)

  const handleMenuClose = () => {
    setMenuKey((prev) => prev + 1)
  }

  useRemoveMenuOverflow()

  return (
    <>
      <Menu
        key={menuKey}
        disabled={disabled}
        button={
          <Button size="xxs" variant="ghost">
            <span className={buttonClassName}>{buttonIcon}</span>
          </Button>
        }
        direction={direction}>
        {type === 'PlatformClientContent' && onPreview && (
          <Menu.Item onClick={onPreview}>
            <div className="flex items-start">
              <HelixIcon icon={eye} className="mr-2" />
              <p className="text-gray-900">Preview</p>
            </div>
          </Menu.Item>
        )}
        {!currentFolder && viewMode === 'table' && (
          <Menu.Item
            onClick={() => {
              setShowSharingModal(true)
              setManagePermissions(null)
            }}>
            <div className="flex items-start">
              <HelixIcon icon={share} className="mr-2" />
              <p className="text-gray-900">Share</p>
            </div>
          </Menu.Item>
        )}
        {type === 'Folder' && (
          <Menu.Item
            onClick={() => {
              navigateToFolder(id)
              uploadRef.current?.click()
            }}>
            <div className="flex items-start">
              <HelixIcon icon={outbox} className="mr-2" />
              <p className="text-gray-900">Upload content</p>
            </div>
          </Menu.Item>
        )}
        {type === 'PlatformClientContent' && (
          <>
            <Menu.Item
              submenu={
                <FolderSubMenu fileId={id} onMenuClose={handleMenuClose} />
              }>
              <div className="flex min-w-[160px] items-start">
                <HelixIcon icon={folder} className="mr-2" />
                <span className="text-gray-900">
                  {currentFolderId ? 'Move to' : 'Add to folder'}
                </span>
              </div>
            </Menu.Item>
          </>
        )}
        <Menu.Item
          onClick={isRenaming ? undefined : onRename}
          disabled={isRenaming}>
          <div className={`flex items-start ${isRenaming ? 'opacity-50' : ''}`}>
            <HelixIcon icon={pencil} className="mr-2" />
            <span className="text-gray-900">Rename</span>
          </div>
        </Menu.Item>
        <Menu.Item variant="danger" onClick={onDelete}>
          <div className="flex items-start text-red-700">
            <HelixIcon
              icon={trash_can}
              className="helix-svg-fill--red-700 mr-2"
            />
            <span>Delete</span>
          </div>
        </Menu.Item>
      </Menu>
      <ShareContentModal
        managePermissions={managePermissions}
        ids={showSharingModal ? [{ id, type }] : []}
        open={showSharingModal || !!managePermissions}
        onClose={() => {
          setShowSharingModal(false)
          setManagePermissions(null)
        }}
      />
    </>
  )
}
